<template>
    <div id="lt-yantr" class="advanced-calc auto-loan-calc">
        <div class="container-width">
            <div class="advanced-calc-filter">
                <label class="filter-header">{{ filterHeader }}</label>
                <ul class="filter-inputs five-col mb-none">
                    <li>
                        <number-input
                            v-model="PurchasePrice"
                            :label="labelPurchasePrice"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="SalesTax"
                            :label="labelSalesTax"
                            symbol="%"
                            symbol-aligned="right"
                            precision="2"
                            max-value="100.00"
                            inputmode="decimal"
                            :tooltip-link="SalesTaxTooltip"
                        />
                        <span class="font-sm">Dollar Amount: <b class="dark-gray-text">${{ currencyWholeNumber(SalesTaxAmount) }}</b></span>
                    </li>
                    <li>
                        <number-input
                            v-model="Registration"
                            :label="labelRegistration"
                            class="b-margin20"
                            :tooltip-link="titleTooltip"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="DownPayment"
                            :label="labelDownPayment"
                            :tooltip-link="DownPaymentTooltip"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <number-input
                            v-model="TradeIn"
                            :label="labelTradeIn"
                            :tooltip-link="netTradeValueTooltip"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <drop-down
                            v-model="CreditScore"
                            :label="labelCreditScore"
                            :options="CreditScoreList"
                        />
                        <a href="/redirect/offers?id=calc-fcs" class="font-sm">Get your credit score</a>
                    </li>
                    <li>
                        <number-input
                            v-model="InterestRate"
                            :label="labelInterestRate"
                            symbol="%"
                            symbol-aligned="right"
                            precision="3"
                            max-value="100.000"
                            inputmode="decimal"
                        />
                    </li>
                    <li>
                        <drop-down
                            v-model="LoanTermInYears"
                            :label="labelLoanTerm"
                            :options="LoanTermInYearsList"
                            :tooltip-link="LoanTermTooltip"
                        />
                    </li>
                </ul>
            </div>
            <div class="row calculation-section">
                <div class="col-xs-12 col-sm-7 left-section">
                    <h4>Your estimated auto payment will be:</h4>
                    <label class="homeprice" :class="{ 'red-text': !isNegativeAmount}">${{ currencyFilterWithPrecision(TotalMoPayment) }}</label>
                    <ul v-if="allTotalPaymentSection==1" class="calc-box">
                        <li>
                            <label>Loan Amount:</label>
                            <h3 class="value">${{ currencyWholeNumber(LoanAmount) }}</h3>
                        </li>
                        <li>
                            <label>Total Payments:</label>
                            <h3 class="value">{{ TotalPayments }}</h3>
                        </li>
                    </ul>
                    <div class="view-free-offers">
                        <h4>We've found offers for this result:</h4>
                        <button class="btn btn-blue btn-arrow" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
                    </div>
                    <div>
                        <advertising-disclosures
                            v-if="DisclosureText"
                            :disclosure-text="DisclosureText"
                            :disclosure-tokens="DisclosureTokens"
                        />
                    </div>
                    <lt-logo v-if="ltLogo==1">
                        <small>NMLS#1136 Terms & Conditions Apply</small>
                    </lt-logo>
                </div>
                <div class="col-xs-12 col-sm-5 right-section text-center">
                    <h4>Total Loan Breakdown</h4>
                    <div class="chart-parent">
                        <vue-chart
                            id="donutChart"
                            type="doughnut"
                            :data="chartData"
                            :options="options"
                            :width="chartWidth"
                            :height="chartHeight"
                        />
                        <div class="donut-inner">
                            <label>Estimated Total Payments:</label>
                            <h3 :class="{ 'red-text': !isNegativeAmount}">${{ currencyWholeNumber(TotalCost) }}</h3>
                        </div>
                    </div>
                    <div class="monthly-budget">
                        <ul class="list-unstyled summary">
                            <li>
                                <label><span class="green-bg"></span> Net Purchase Price:</label>
                                <span class="value">${{ currencyWholeNumber(NetPurchasePrice) }}</span>
                            </li>
                            <li>
                                <label><span class="red-bg"></span> {{ labelRegistration }}</label>
                                <span class="value">${{ currencyWholeNumber(Registration) }}</span>
                            </li>
                            <li>
                                <label><span class="blue-bg"></span> Total Interest:</label>
                                <span class="value">${{ currencyWholeNumber(TotalInterest) }}</span>
                            </li>
                            <li>
                                <label><span class="purple-bg"></span> Total Sales Tax:</label>
                                <span class="value">${{ currencyWholeNumber(SalesTaxAmount) }}</span>
                            </li>
                        </ul>
                        <div class="clear-fix"></div>
                    </div>
                </div>
                <div v-if="howToUseCalculator==1" class="col-xs-12 about-calculator text-center">
                    <p class="sub-heading">Use this auto calculator to estimate your monthly auto payments, including estimates for taxes, and trade-ins.</p>
                    <a v-vue-scroll-to="{ el:'#how-to-use-auto-loan-calculator', offset: -90 }">How to use this calculator</a>
                </div>
            </div>
            <div class="full-width-border"></div>
            <auto-amortization-chart
                v-if="amortizationChartSection==1"
                :loan-amount="LoanAmount"
                :loan-term-in-years="LoanTermInYears"
                :interest-rate="InterestRate"
            />
            <auto-amortization-table
                v-if="amortizationTableSection==1"
                :loan-amount="LoanAmount"
                :loan-term-in-years="LoanTermInYears"
                :interest-rate="InterestRate"
            />
            <monthly-payment-by-term-table
                v-if="paymentTermComparisonSection==1"
                :payment-data="MonthlyPaymentComparison"
                @updateCalculator="updateCalculator"
            />
        </div>
    </div>
</template>

<script>
    import VueChart from 'vue-chart-js';
    import Accounting from 'accounting';
    import VueScrollTo from 'vue-scrollto';
    import AutoAmortizationChart from '~/components/auto-amortization-chart.vue';
    import AutoAmortizationTable from '~/components/auto-amortization-table.vue';
    import MonthlyPaymentByTermTable from './components/monthly-payment-by-term-table.vue';
    import NumberInput from '~/components/number-input.vue';
    import DropDown from '~/components/drop-down.vue';
    import ChartTooltip from '~/helpers/chart-tooltip';
    import BaseMixin from '~/mixin/base-mixin';
    import LtLogo from '~/components/lt-logo.vue';
    import AdvertisingDisclosures from '~/components/advertising-disclosures.vue';
    import LoanTermList from '~/constants/loan-term-list';
    import CreditScoreList from '~/constants/credit-score-list';
    import DefaultTooltip from '~/constants/tooltip-defaults';

    export default {
        components: {
            NumberInput,
            DropDown,
            VueChart,
            AutoAmortizationChart,
            AutoAmortizationTable,
            MonthlyPaymentByTermTable,
            LtLogo,
            AdvertisingDisclosures,
        },
        directives: {
            VueScrollTo,
        },
        mixins: [BaseMixin],
        props: {
            filterHeader: {
                default: 'Auto Information',
            },
            labelPurchasePrice: {
                default: 'Purchase Price:',
            },
            labelSalesTax: {
                default: 'Sales Tax Percentage:',
            },
            labelRegistration: {
                default: 'Title & Registration:',
            },
            labelDownPayment: {
                default: 'Down Payment:',
            },
            labelTradeIn: {
                default: 'Net Trade-In Value:',
            },
            labelCreditScore: {
                default: 'Credit Score:',
            },
            labelInterestRate: {
                default: 'Interest Rate:',
            },
            labelLoanTerm: {
                default: 'Loan Term:',
            },
            loanAmount: {
                default: 26064,
            },
            salesTax: {
                default: 8.6,
            },
            salesTaxAmount: {
                default: 2064,
            },
            registration: {
                default: 0,
            },
            downPayment: {
                default: 0,
            },
            tradeIn: {
                default: 1000,
            },
            interestRate: {
                default: 2.5,
            },
            creditScore: {
                default: '1',
            },
            loanTermInYears: {
                default: 4,
            },
            span: {
                type: String,
                default: '',
            },
            maxValue: {
                default: 80,
            },
            ltLogo: {
                default: 0,
            },
            disclosureTokens: {
                default: '',
            },
            disclosureText: {
                default: '',
            },
            amortizationChartSection: {
                default: 1,
            },
            amortizationTableSection: {
                default: 1,
            },
            allTotalPaymentSection: {
                default: 1,
            },
            paymentTermComparisonSection: {
                default: 1,
            },
            howToUseCalculator: {
                default: 1,
            },
            totalMoPayment: {
                default: 571.17,
            },
            totalPayments: {
                default: 48,
            },
            totalInterest: {
                default: 1352.16,
            },
            salesTaxTooltip: {
                default: DefaultTooltip.SalesTax,
            },
            titleTooltip: {
                default: DefaultTooltip.Title,
            },
            downPaymentTooltip: {
                default: DefaultTooltip.DownPayment,
            },
            netTradeValueTooltip: {
                default: DefaultTooltip.NetTradeValue,
            },
            loanTermTooltip: {
                default: DefaultTooltip.LoanTerm,
            },
            purchasePrice: {
                default: 25000,
            },
            netPurchasePrice: {
                default: 24000,
            },
            totalCost: {
                default: 27416.16,
            },
            monthlyPaymentComparison: {
                type: Object,
                default() {
                    return {
                        rates: [2201.53, 1114.51, 752.24, 571.17, 462.57, 390.21],
                    };
                },
            },
            destinationLinkOrId: {
                default: 'wp-auto',
            },
            destinationButtonText: {
                default: 'Compare Loan Offers',
            },
        },
        data() {
            return {
                LoanTermInYears: this.loanTermInYears,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList.autoloanterm,
                CreditScoreList: CreditScoreList.autoloan,
                TotalPayments: this.totalPayments,
                SalesTax: this.salesTax,
                SalesTaxAmount: this.salesTaxAmount,
                Registration: this.registration,
                DownPayment: this.downPayment,
                CreditScore: this.creditScore,
                LoanAmount: this.loanAmount,
                TradeIn: this.tradeIn,
                TotalInterest: this.totalInterest,
                InterestRate: this.interestRate,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                SalesTaxTooltip: parseInt(this.hideTooltip, 10) ? '' : this.salesTaxTooltip,
                TitleTooltipDefault: parseInt(this.hideTooltip, 10) ? '' : this.titleTooltip,
                DownPaymentTooltip: parseInt(this.hideTooltip, 10) ? '' : this.downPaymentTooltip,
                NetTradeValueTooltip: parseInt(this.hideTooltip, 10) ? '' : this.netTradeValueTooltip,
                LoanTermTooltip: parseInt(this.hideTooltip, 10) ? '' : this.loanTermTooltip,
                PurchasePrice: this.purchasePrice,
                NetPurchasePrice: this.netPurchasePrice,
                TotalMoPayment: this.totalMoPayment,
                TotalCost: this.totalCost,
                MonthlyPaymentComparison: this.monthlyPaymentComparison,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
                // Setting for chart
                chartData: {
                    labels: [],
                    datasets: [
                        {
                            data: [],
                            backgroundColor: [
                                '#08c177',
                                '#da3551',
                                '#2087c1',
                                '#00aeef',

                            ],
                            borderWidth: 0,
                        },
                    ],
                },
                options: {
                    animation: {
                        duration: 1000,
                    },
                    cutoutPercentage: 89,
                    legend: {
                        display: false,
                    },
                    tooltips: {
                        enabled: false,
                        custom: ChartTooltip.customTooltips,
                    },
                },
                chartWidth: 200,
                chartHeight: 130,
                CurrentDate: new Date(),
                PaymentFrequency: 'monthly',
            };
        },
        computed: {
            isNegativeAmount() {
                return this.TotalMoPayment >= 0;
            },
        },
        watch: {
            PurchasePrice() {
                this.updateCalculator();
            },
            Registration() {
                this.updateCalculator();
            },
            SalesTax() {
                this.updateCalculator();
            },
            TradeIn() {
                this.updateCalculator();
            },
            DownPayment() {
                this.updateCalculator();
            },
            LoanTermInYears() {
                this.updateCalculator();
            },
            InterestRate() {
                this.updateCalculator();
            },
        },
        mounted() {
            if (this.chartData && this.chartData.datasets && this.chartData.datasets[0] && this.chartData.datasets[0].data) {
                this.chartData.datasets[0].data.push(Math.round(Math.abs(this.NetPurchasePrice / this.TotalCost * 100)));
                this.chartData.datasets[0].data.push(Math.round(Math.abs(this.Registration / this.TotalCost * 100)));
                this.chartData.datasets[0].data.push(Math.round(Math.abs(this.SalesTaxAmount / this.TotalCost * 100)));
                this.chartData.datasets[0].data.push(Math.round(Math.abs(this.TotalInterest / this.TotalCost * 100)));
                this.chartData.labels.push(`Net Purchase Price ${Accounting.formatMoney(this.NetPurchasePrice)}`);
                this.chartData.labels.push(`Title & Registration ${Accounting.formatMoney(this.Registration)}`);
                this.chartData.labels.push(`Total Sales Tax ${Accounting.formatMoney(this.SalesTaxAmount)}`);
                this.chartData.labels.push(`Total Interest ${Accounting.formatMoney(this.TotalInterest)}`);
            }
        },
        methods: {
            unformat(val) {
                return Accounting.unformat(val);
            },
            capitalize(value) {
                if (!value) return '';
                return value.toString().charAt(0).toUpperCase() + value.toString().slice(1);
            },
            currencyFilterWithPrecision(figure) {
                return figure > 0 ? Accounting.formatNumber(figure, { precision: 2 }) : 0;
            },
            currencyWholeNumber(figure) {
                return figure > 0 ? Accounting.formatNumber(figure) : 0;
            },
            updateNetPurchasePrice() {
                this.chartData.datasets[0].data[0] = Math.round(Math.abs(this.NetPurchasePrice / this.TotalCost * 100));
                this.chartData.labels[0] = `Net Purchase Price ${Accounting.formatMoney(this.NetPurchasePrice)}`;
            },
            updateRegistration() {
                this.chartData.datasets[0].data[1] = Math.round(Math.abs(parseFloat(this.unformat(this.Registration) / this.TotalCost) * 100));
                this.chartData.labels[1] = `Title & Registration ${Accounting.formatMoney(parseFloat(this.unformat(this.Registration)))}`;
            },
            updateTotalInterest() {
                this.chartData.datasets[0].data[3] = Math.round(Math.abs(this.TotalInterest / this.TotalCost * 100));
                this.chartData.labels[3] = `Total Interest ${Accounting.formatMoney(this.TotalInterest)}`;
            },
            updateTotalSalesTax() {
                this.chartData.datasets[0].data[2] = Math.round(Math.abs(this.SalesTaxAmount / this.TotalCost * 100));
                this.chartData.labels[2] = `Total Sales Tax ${Accounting.formatMoney(this.SalesTaxAmount)}`;
            },
            calculatePrincipal() {
                const principal = parseFloat(this.unformat(this.PurchasePrice)) + parseFloat(this.unformat(this.Registration)) + this.SalesTaxAmount - parseFloat(this.unformat(this.DownPayment)) - parseFloat(this.unformat(this.TradeIn));
                return principal >= 0 ? principal : 0;
            },
            calculatePayment(monthlyPayments) {
                this.InterestRate = this.InterestRate > 100 ? 100 : this.InterestRate;
                const rate = this.InterestRate / 100;
                let paymentAmt;
                if (rate > 0) paymentAmt = this.LoanAmount * (rate / 12) / (1 - (1 + rate / 12) ** -monthlyPayments);
                else paymentAmt = this.LoanAmount / this.TotalPayments;
                return paymentAmt >= 0 ? paymentAmt : 0;
            },
            // Note: This assumes trade-in value is counted as a tax credit toward purchase
            calculateSalesTax() {
                this.SalesTax = this.SalesTax > 100 ? 100 : this.SalesTax;
                const salesTax = (parseFloat(this.unformat(this.PurchasePrice)) - parseFloat(this.unformat(this.TradeIn))) * (parseFloat(this.SalesTax) / 100);
                return salesTax >= 0 ? salesTax : 0;
            },
            calculateTotalInterest(monthlyPayments) {
                const totalInterest = this.TotalMoPayment * monthlyPayments - this.calculatePrincipal();
                return totalInterest >= 0 ? totalInterest : 0;
            },
            updateCalculator() {
                this.SalesTaxAmount = this.calculateSalesTax();
                this.LoanAmount = this.calculatePrincipal();

                if (this.LoanAmount <= 0) {
                    this.TotalPayments = 1;
                } else {
                    this.TotalPayments = this.LoanTermInYears * 12;
                }

                this.NetPurchasePrice = this.calculateNetPurchasePrice();
                this.TotalMoPayment = this.calculatePayment(this.TotalPayments);
                this.TotalInterest = this.calculateTotalInterest(this.TotalPayments);
                this.TotalCost = this.calculateTotalCost();
                this.calculateMonthlyPaymentComparison();

                // update chart
                this.chartData.labels = [];
                this.chartData.datasets[0].data = [];
                this.updateNetPurchasePrice();
                this.updateRegistration();
                this.updateTotalInterest();
                this.updateTotalSalesTax();
            },
            calculateMonthlyPaymentComparison() {
                const paymentmonths = [12, 24, 36, 48, 60, 72];
                const paymentData = [];

                paymentmonths.forEach((val) => {
                    const paymentAmt = this.calculatePayment(val);
                    paymentData.push(paymentAmt);
                });
                this.MonthlyPaymentComparison.rates = paymentData;
            },
            calculateNetPurchasePrice() {
                const netPurchasePrice = parseFloat(this.unformat(this.PurchasePrice)) - parseFloat(this.unformat(this.DownPayment)) - parseFloat(this.unformat(this.TradeIn));
                return netPurchasePrice >= 0 ? netPurchasePrice : 0;
            },
            calculateTotalCost() {
                const totalCost = this.NetPurchasePrice + this.SalesTaxAmount + this.TotalInterest + parseFloat(this.unformat(this.Registration));
                return totalCost >= 0 ? totalCost : 0;
            },
        },
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/calc-advanced-filter.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
